<template>
    <div>
        <v-data-table
            class="billing-guide-transactions-table"
            :headers="headers"
            :items="transactions"
            :items-per-page="500"
            hide-default-footer
            fixed-header
            :disable-pagination="true"
        >
            <template v-slot:item.name="{ item }">
                {{ getTransactionDisplayName(item) }}
            </template>
            <template
              v-if="showBusiness"
              v-slot:item.place="{ item }"
            >
              <template v-if="canViewBusiness(item)">
                  <a
                    :href="getSingleBusinessUrl(item.place_id)"
                    target="_blank"
                  >
                    {{ item.place.name }}
                  </a>
              </template>
              <template v-else>
                  {{ item.place.name }}
              </template>
              <div v-if="item.place.location && item.place.location.shortFormattedAddress" class="caption">
                {{ item.place.location.shortFormattedAddress }}
              </div>
            </template>
            <template
              v-if="showSubscription"
              v-slot:item.subscription="{ item }"
            >
              <router-link
                  v-if="item.subscription"
                  :to="{
                    name: 'my-subscriptions.subscription-single',
                    params: { id: item.subscription.id, returnTo: { name: 'my-subscriptions' } }
                  }"
                >
                  <v-chip small class="cursor-pointer hover-underline">
                    {{ getPlanName(item) }}
                  </v-chip>
                </router-link>
            </template>
            <template v-slot:item.status="{ item }">
                <v-chip
                    small
                    class="text-uppercase"
                    :class="{
                      'black white--text': item.data.status === TRANSACTION_STATUS_COMPLETED,
                    }"
                >
                    {{ $t(`TransactionStatus.${item.data.status}`) }}
                </v-chip>
            </template>
            <template v-slot:item.period="{ item }">
                <span v-if="
                  !isZeroValuePaymentMethodUpdate(item)
                  && item.data.billing_period
                  && item.data.billing_period.starts_at
                  && item.data.billing_period.ends_at
                ">
                  {{ fullDateFormat(item.data.billing_period.starts_at) }} -
                  {{ fullDateFormat(item.data.billing_period.ends_at) }}
                </span>
            </template>
            <template v-slot:item.created_at="{ item }">
                {{ fullDateFormat(item.data.created_at || item.created_at) }}
            </template>
            <template v-slot:item.amount="{ item }">
                <div class="text-no-wrap">
                    {{ item.formatted_pricing.sum_total }}
                </div>
            </template>
            <template v-slot:item.actions="{ item, index }">
                <div class="d-flex">
                    <v-btn
                        v-if="canDownloadInvoice(item)"
                        class="text-capitalize cursor-pointer mr-2 billing-guide-invoice-btn single-subscription-guide-invoice-btn"
                        :class="{
                          'billing-guide-invoice-btn-first': isFirstDownloadableInvoice(item, index),
                          'single-subscription-guide-invoice-btn-first': isFirstDownloadableInvoice(item, index),
                        }"
                        @click="downloadInvoice(item.id)"
                    >
                        <v-icon left>mdi-download</v-icon>
                        {{ $t('Invoice') }}
                    </v-btn>
                </div>
            </template>
        </v-data-table>
    </div>
</template>

<script>
import axios from 'axios';
import { fullDateFormat } from '@/components/calendar/config';
import CurrencyMixin from '@/lib/CurrencyMixin';
import SubscriptionsMixin from '@/lib/SubscriptionsMixin';

export default {
  mixins: [CurrencyMixin, SubscriptionsMixin],
  props: {
    transactions: {
      type: Array,
      required: true,
    },
    showBusiness: {
      type: Boolean,
      default: true,
    },
    showSubscription: {
      type: Boolean,
      default: true,
    },
  },
  computed: {
    headers() {
      const headers = [{ text: this.$t('Form Info Name'), value: 'name' }];
      if (this.showBusiness) {
        headers.push({ text: this.$t('Business'), value: 'place' });
      }
      if (this.showSubscription) {
        headers.push({ text: this.$t('Subscription'), value: 'subscription' });
      }
      headers.push(
        { text: 'Status', value: 'status' },
        { text: this.$t('Price'), value: 'amount' },
        { text: this.$t('Period'), value: 'period' },
        { text: this.$t('Date'), value: 'created_at' },
        { text: '', value: 'actions', sortable: false },
      );
      return headers;
    },
  },
  methods: {
    fullDateFormat,
    isFirstDownloadableInvoice(item, index) {
      return this.canDownloadInvoice(item)
        && this.transactions.findIndex((transaction) => this.canDownloadInvoice(transaction)) === index;
    },
    downloadInvoice(id) {
      this.$store.dispatch('loading', true);

      const newWindow = window.open('', '_blank');

      axios
        .get(`/transactions/${id}/invoice`)
        .then(({ data }) => {
          this.$store.dispatch('loading', false);

          if (data.invoice_url) {
            if (window.isUsingApp) {
              if (window.cordova_iab) {
                window.cordova_iab.postMessage(JSON.stringify({ url: data.invoice_url, type: 'pdf' }));
              } else if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.cordova_iab) {
                window.webkit.messageHandlers.cordova_iab.postMessage(JSON.stringify({ url: data.invoice_url, type: 'pdf' }));
              }
            } else if (newWindow) {
              newWindow.location.href = data.invoice_url;
            }
          }

          setTimeout(() => {
            newWindow.close();
          }, 500);
        })
        .catch((error) => {
          if (newWindow) {
            newWindow.close();
          }
          this.$store.dispatch('message', error.response.data && error.response.data.message
            ? error.response.data.message
            : this.$t('Server error'));
          this.$store.dispatch('loading', false);
        });
    },
  },
};
</script>
